// NOTE: this parse file could use more cleaning and simplifying
// keeping as is for now to prevent breaking anything and time constraints
import { getFullElectionTitle, getShortElectionTitle } from "@/helpers/titles";
import { store } from "@/store";

const ELECTION_TYPES = {
  1: "general",
  2: "primary",
  3: "presidential primary",
  5: "runoff",
  6: "jungle",
  8: "special primary",
  9: "special general",
  10: "presidential primary",
  12: "presidential primary",
};

const getVoteTotal = (votes) => Object.values(votes).reduce((a, b) => a + b);

const getCandidatePercentages = (
  candidates,
  { votes, called, called_candidates, totalVotes, runoff_candidates, advancing_candidates }
) => {
  const candidateResults = candidates
    .map((c) => {
      const candidateVotes = votes[c.cand_id];
      let percentage = 0.0;
      if (totalVotes) {
        percentage = (candidateVotes / totalVotes) * 100;
      }
      return {
        ...c,
        percentage,
        votes: candidateVotes,
        incumbent: c.incumbent > 0,
      };
    })
    .sort((a, b) => {
      return b.percentage - a.percentage;
    });
  return candidateResults.map((candidate, index) => {
    // const finished = precincts && precincts.reporting === precincts.total
    let winning = index === 0 && totalVotes > 0;
    if (index === 1 && totalVotes > 0 && candidateResults[0].votes === candidate.votes) {
      winning = true;
    }

    let winner = null;
    // new logic
    if (advancing_candidates) {
      winner =
        called_candidates?.includes(candidate.cand_id) ||
        advancing_candidates?.includes(candidate.cand_id);
    } else {
      // old logic
      winner = called && candidate.cand_id === called;
      if (typeof runoff_candidates !== "undefined") {
        winner = winner || runoff_candidates.includes(candidate.cand_id);
      }
    }

    return {
      ...candidate,
      winning,
      winner,
    };
  });
};

const supremeCourtOfficeID = 30;
const courtOfficeIDs = [supremeCourtOfficeID, 132, 133, 152, 153, 154];
const partisanSupremeCourtStates = ["Pennsylvania"];

function isNonPartisanCourt(officeId, state, party) {
  const isCourtOffice = courtOfficeIDs.includes(officeId);

  if (!isCourtOffice) return false;

  const isSupremeCourt = officeId === 30;

  if (isSupremeCourt) {
    const isPartisan = partisanSupremeCourtStates.includes(state);

    return !isPartisan;
  }

  if (party !== "Nonpartisan") return false;

  return true;
}

const yesCandidates = ["affirmative", "yes"];

function cleanCandidateName(name) {
  if (!name) return "";

  return name.trim().toLowerCase();
}

function isYesCandidate(firstName, lastName) {
  if (firstName === "question 1" && lastName === "yes") return true;

  if (yesCandidates.includes(firstName) && (lastName === "" || yesCandidates.includes(lastName))) {
    return true;
  }

  if (yesCandidates.includes(lastName) && (firstName === "" || yesCandidates.includes(firstName))) {
    return true;
  }

  return false;
}

function isNoCandidate(firstName, lastName) {
  if (firstName === "no" && (lastName === "no" || lastName === "")) {
    return true;
  }

  if (lastName === "no" && (firstName === "no" || firstName === "")) {
    return true;
  }

  return false;
}

const getCandidateColors = (clients, candidates, electionType, officeId, party, state) => {
  const candOverrides = store.getters.colors.primary.candidateOverrides;
  const partyColors = store.getters.colors.party;

  const demColors = store.getters.colors.primary.democratic;
  const gopColors = store.getters.colors.primary.republican;

  const counter = {
    democratic: 0,
    republican: 0,
  };

  const a = candidates
    .sort((candidateA, candidateB) => {
      return (
        candOverrides.includes(candidateB.cand_id) - candOverrides.includes(candidateA.cand_id)
      );
    })
    .map((c) => {
      let color;

      const cleanedFirstName = cleanCandidateName(c.first_name);
      const cleanedLastName = cleanCandidateName(c.last_name);

      if (isYesCandidate(cleanedFirstName, cleanedLastName)) {
        if (c.last_name.length === 0) c.last_name = "Yes";
        if (c.first_name.length === 0) c.first_name = "Yes";
        color = partyColors.Green;
      } else if (isNoCandidate(cleanedFirstName, cleanedLastName)) {
        if (c.last_name.length === 0) c.last_name = "No";
        if (c.first_name.length === 0) c.first_name = "No";
        color = partyColors.Republican;
      } else if (isNonPartisanCourt(officeId, state, party)) {
        if (candOverrides.includes(c.cand_id)) {
          const color = `var(--${c.cand_id})`;
          return {
            ...c,
            color,
          };
        } else {
          // only use colors from democratic color palette
          // to avoid candidates getting colors assigned according
          // to their party affiliation
          // since these races are supposed to be non-partisan
          // TODO: come up with a better way to assign colors in these scenarios (third-party color palette?)
          color = demColors[counter.democratic++ % demColors.length];
          return {
            ...c,
            color,
          };
        }
      } else if (
        electionType === "general" ||
        electionType === "special general" ||
        (electionType === "runoff" && !party)
      ) {
        color = c.party_name in partyColors ? partyColors[c.party_name] : partyColors.Other;
      } else if (
        ["primary", "jungle", "presidential primary", "special primary"].includes(electionType) ||
        (electionType === "runoff" && party)
      ) {
        // check to see if candidate id is in color overrides
        if (candOverrides.includes(c.cand_id)) {
          const color = `var(--${c.cand_id})`;

          if (c.party_name === "Republican") {
            counter.republican++;
          } else {
            counter.democratic++;
          }

          return {
            ...c,
            color,
          };
          // if candidate is not in overrides grab color from default primary list
          // combine default colors list with used colors to remove duplicates
        } else {
          if (c.party_name === "Republican") {
            color = gopColors[counter.republican % gopColors.length];
            counter.republican++;
          } else {
            color = demColors[counter.democratic % demColors.length];
            counter.democratic++;
          }
        }
      } else {
        color = "#fff";
      }

      return {
        ...c,
        color,
      };
    });

  return a;
};

const getPrecinctsReporting = (precincts) => {
  const total = parseInt(precincts && precincts.total ? precincts.total : 0);
  const reporting = parseInt(precincts && precincts.reporting ? precincts.reporting : 0);
  const percentage = total > 0 ? reporting / total : 0.0;
  const precinctsObj = {
    hide: false,
    overrideText: null,
    total,
    reporting,
    percentage: percentage * 100,
  };
  return precinctsObj;
};

export function processResults(
  clients,
  election,
  results,
  called,
  called_candidates,
  rename,
  electionCandidates
) {
  const data = { ...results, year: election.year };
  if (typeof called !== "undefined") {
    data.called = called;
  }
  data.called_candidates = called_candidates || results.called_candidates;
  data.totalVotes = getVoteTotal(results.votes);
  data.candidates = getCandidatePercentages(election.candidates, data);
  // get color for candidate county results from election object
  if (electionCandidates) {
    data.candidates = data.candidates.map((item) => {
      const candidate = electionCandidates.find((c) => c.cand_id === item.cand_id);
      let lastNameOverride = null;
      if (candidate.last_name === "Afirmative") {
        lastNameOverride = "Yes";
      }
      return {
        ...item,
        last_name: lastNameOverride !== null ? lastNameOverride : candidate.last_name,
        color: candidate.color,
      };
    });
  } else {
    data.electionType = ELECTION_TYPES[election.election_type_id];
    data.candidates = getCandidateColors(
      clients,
      data.candidates,
      data.electionType,
      election.office_id,
      election.party,
      election.state_name
    );
  }
  data.precincts = getPrecinctsReporting(results.precincts);
  data.reporting = data.precincts.percentage;
  if (typeof rename !== "undefined") {
    Object.entries(rename).forEach(([oldName, newName]) => {
      data[newName] = data[oldName];
    });
  }

  return data;
}

export function processElection(params, election, countyResults, toplineResults, vcuResults) {
  const results = processResults(params.clients, election, toplineResults);

  results.short_title = getShortElectionTitle(election);
  results.full_title = getFullElectionTitle(election);

  const candidateOrder = results.candidates.map((c) => c.cand_id);
  const detailedResults = countyResults || vcuResults;

  if (detailedResults) {
    results.counties = detailedResults.map((vcu) => {
      const data = processResults(
        params.clients,
        election,
        vcu,
        results.called,
        results.called_candidates,
        { county: "name" },
        results.candidates
      );

      data.candidates = data.candidates.sort((a, b) => {
        return candidateOrder.indexOf(a.cand_id) - candidateOrder.indexOf(b.cand_id);
      });

      return data;
    });
  }

  if (vcuResults) {
    const vcusByCounty = vcuResults.counties.map((item) => {
      const county = item.county;
      item.vcus.forEach((vcu) => {
        vcu.county = county;
      });
      return item.vcus;
    });
    const vcus = [].concat.apply([], vcusByCounty);
    results.vcus = vcus;
    results.vcus = results.vcus.map((precinct) => {
      const totalVotes = Object.values(precinct.votes).reduce(
        (acc, cur) => parseInt(acc) + parseInt(cur)
      );
      return {
        ...precinct,
        totalVotes,
      };
    });

    results.vcus = results.vcus
      .filter((c) => c.vcu !== "")
      .map((county) => {
        const data = processResults(
          params.clients,
          election,
          county,
          results.called,
          results.called_candidates,
          { vcu: "name" },
          results.candidates
        );
        // Sort candidates to match overall results
        data.candidates = data.candidates.sort((a, b) => {
          return candidateOrder.indexOf(a.cand_id) - candidateOrder.indexOf(b.cand_id);
        });
        return data;
      });
  }

  return { ...election, ...results };
}
